/**
 * Subscription Context for trAInedup.ai
 * Manages subscription state and provides subscription-related functionality
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { db } from '../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import {
  SubscriptionData,
  getCurrentSubscription,
  redirectToCheckout,
  updateSubscription,
  cancelSubscription,
  reactivateSubscription,
  createCustomerPortalSession,
  getPriceIdFromTier,
} from '../services/stripe/subscriptions';
import { SubscriptionTier, SUBSCRIPTION_TIERS } from '../services/stripe/config';

/**
 * Subscription context interface
 */
interface SubscriptionContextType {
  // Subscription state
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;

  // Subscription status checks
  isSubscriptionActive: () => boolean;
  hasFeatureAccess: (feature: string) => boolean;
  canAccessTier: (tier: SubscriptionTier) => boolean;
  isTrialing: () => boolean;
  isCanceled: () => boolean;

  // Subscription actions
  subscribe: (tier: SubscriptionTier) => Promise<void>;
  changePlan: (newTier: SubscriptionTier) => Promise<void>;
  cancelPlan: (cancelAtPeriodEnd?: boolean) => Promise<void>;
  reactivatePlan: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  refreshSubscription: () => Promise<void>;

  // Subscription utilities
  getCurrentTier: () => SubscriptionTier;
  getDaysUntilRenewal: () => number;
  getTrialDaysRemaining: () => number;
  // Pending downgrade info
  pendingDowngradeTier?: SubscriptionTier | null;
  pendingDowngradeEffectiveDate?: Date | null;
  cancelScheduledDowngrade?: () => Promise<void>;
}

/**
 * Feature access mapping by subscription tier
 */
const FEATURE_ACCESS: Record<string, string[]> = {
  free: [
    'basic_courses',
    'community_access',
    'basic_support',
  ],
  standard: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
  ],
  premium: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses',
    'expert_consultations',
    'priority_support',
    'custom_learning_paths',
    'certificates',
  ],
  annual: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses',
    'expert_consultations',
    'priority_support',
    'custom_learning_paths',
    'certificates',
  ],
  twelve_month_package: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses', // Can see all courses (uses credits to enroll)
    'certificates',
  ],
  stackcommerce_lifetime: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
  ],
  corporate: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses',
    'expert_consultations',
    'priority_support',
    'custom_learning_paths',
    'certificates',
    'team_management',
    'advanced_analytics',
    'white_label',
    'dedicated_support',
    'custom_integrations',
  ],
  // Legacy aliases for backward compatibility
  starter: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
  ],
  basic: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
  ],
  professional: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses',
    'expert_consultations',
    'priority_support',
    'custom_learning_paths',
    'certificates',
  ],
  enterprise: [
    'basic_courses',
    'community_access',
    'basic_support',
    'ai_fundamentals',
    'monthly_training_pack',
    'progress_tracking',
    'advanced_courses',
    'expert_consultations',
    'priority_support',
    'custom_learning_paths',
    'certificates',
    'team_management',
    'advanced_analytics',
    'white_label',
    'dedicated_support',
    'custom_integrations',
  ],
};

/**
 * Subscription context
 */
const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

/**
 * Subscription provider props
 */
interface SubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Subscription provider component
 */
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDowngradeTier, setPendingDowngradeTier] = useState<SubscriptionTier | null>(null);
  const [pendingDowngradeEffectiveDate, setPendingDowngradeEffectiveDate] = useState<Date | null>(null);

  /**
   * Load user's subscription data
   */
  const loadSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const subscriptionData = await getCurrentSubscription();
      // Also fetch pending downgrade flags from user doc
      try {
        const userRef = doc(db, 'users', user!.uid);
        const userSnap = await getDoc(userRef);
        const u: any = userSnap.data() || {};
        setPendingDowngradeTier(u.pendingDowngradeTier || null);
        const eff = u.pendingDowngradeEffectiveDate;
        const toDate = (v: any) => v?.toDate ? v.toDate() : (typeof v === 'number' ? new Date(v < 2e10 ? v * 1000 : v) : (v ? new Date(v) : null));
        setPendingDowngradeEffectiveDate(toDate(eff));
      } catch (_) {}
      setSubscription(subscriptionData);
    } catch (err: any) {
      console.error('Error loading subscription:', err);
      setError(err.message || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  /**
   * Load subscription on auth state change
   */
  useEffect(() => {
    loadSubscription();
  }, [isAuthenticated, user, loadSubscription]);

  /**
   * Check if subscription is active
   */
  const isSubscriptionActive = (): boolean => {
    if (!subscription) {
      if (user && user.subscriptionTier && user.subscriptionTier !== SUBSCRIPTION_TIERS.FREE) {
        return true;
      }
      return false;
    }
    // Treat Stripe status as source of truth; avoid gating on period end to prevent brief misclassification
    return subscription.status === 'active' || subscription.status === 'trialing';
  };

  /**
   * Check if user has access to a specific feature
   */
  const hasFeatureAccess = (feature: string): boolean => {
    const currentTier = getCurrentTier();
    const tierFeatures = FEATURE_ACCESS[currentTier] || [];
    return tierFeatures.includes(feature);
  };

  /**
   * Check if user can access a specific tier's features
   */
  const canAccessTier = (tier: SubscriptionTier): boolean => {
    const currentTier = getCurrentTier();
    
    // Handle legacy tier names and map them to new naming
    const normalizeCurrentTier = (tier: string): string => {
      if (tier === 'starter' || tier === 'basic') return 'standard';
      if (tier === 'professional') return 'premium';
      if (tier === 'enterprise') return 'corporate';
      return tier;
    };
    
    const normalizedCurrentTier = normalizeCurrentTier(currentTier);
    // Tier hierarchy: twelve_month_package can access all courses (with credits)
    // Position it at premium level for visibility, but enrollment is controlled by credits
    const tierHierarchy = ['free', 'standard', 'stackcommerce_lifetime', 'twelve_month_package', 'premium', 'annual', 'corporate'];
    const currentIndex = tierHierarchy.indexOf(normalizedCurrentTier);
    const requiredIndex = tierHierarchy.indexOf(tier);

    // Special case: twelve_month_package users can VIEW all courses (they'll use credits to enroll)
    if (normalizedCurrentTier === 'twelve_month_package') {
      return true; // Can view/access all courses
    }

    if (currentIndex === -1) return false;
    return currentIndex >= requiredIndex && requiredIndex !== -1;
  };

  /**
   * Check if user is in trial period
   */
  const isTrialing = (): boolean => {
    if (!subscription) return false;
    return subscription.status === 'trialing';
  };

  /**
   * Check if subscription is canceled
   */
  const isCanceled = (): boolean => {
    if (!subscription) return false;
    return subscription.cancelAtPeriodEnd || subscription.status === 'canceled';
  };

  /**
   * Subscribe to a plan
   */
  const subscribe = async (tier: SubscriptionTier): Promise<void> => {
    try {
      setError(null);
      const priceId = getPriceIdFromTier(tier);
      await redirectToCheckout(priceId, tier);
    } catch (err: any) {
      setError(err.message || 'Failed to start subscription');
      throw err;
    }
  };

  /**
   * Change subscription plan
   */
  const changePlan = async (newTier: SubscriptionTier): Promise<void> => {
    try {
      setError(null);
      const newPriceId = getPriceIdFromTier(newTier);
      try {
        await updateSubscription(newPriceId, newTier);
      } catch (err: any) {
        // Fallback: if no active subscription, start a new checkout
        console.warn('[Subscription] update failed, falling back to checkout:', err?.message || err);
        await redirectToCheckout(newPriceId, newTier);
        return; // will redirect to Stripe
      }
      await loadSubscription(); // Refresh subscription data
    } catch (err: any) {
      setError(err.message || 'Failed to change plan');
      throw err;
    }
  };

  /**
   * Cancel subscription
   */
  const cancelPlan = async (cancelAtPeriodEnd: boolean = true): Promise<void> => {
    try {
      setError(null);
      await cancelSubscription(cancelAtPeriodEnd);
      await loadSubscription(); // Refresh subscription data
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
      throw err;
    }
  };

  /**
   * Reactivate subscription
   */
  const reactivatePlan = async (): Promise<void> => {
    try {
      setError(null);
      await reactivateSubscription();
      await loadSubscription(); // Refresh subscription data
    } catch (err: any) {
      setError(err.message || 'Failed to reactivate subscription');
      throw err;
    }
  };

  /**
   * Open Stripe customer portal
   */
  const openCustomerPortal = async (): Promise<void> => {
    try {
      setError(null);
      const { url } = await createCustomerPortalSession();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to open customer portal');
      throw err;
    }
  };

  /**
   * Refresh subscription data
   */
  const refreshSubscription = async (): Promise<void> => {
    await loadSubscription();
  };

  /**
   * Get current subscription tier
   */
  const getCurrentTier = (): SubscriptionTier => {
    if (!subscription) {
      // Fallback to user's subscriptionTier field if no subscription document exists
      if (user && (user as any).subscriptionTier) {
        return (user as any).subscriptionTier as SubscriptionTier;
      }
      return SUBSCRIPTION_TIERS.FREE;
    }
    return subscription.tier;
  };

  /**
   * Get days until subscription renewal
   */
  const getDaysUntilRenewal = (): number => {
    if (!subscription) return 0;
    
    const now = new Date();
    const renewalDate = subscription.currentPeriodEnd;
    const diffTime = renewalDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  /**
   * Get trial days remaining
   */
  const getTrialDaysRemaining = (): number => {
    if (!subscription || !subscription.trialEnd) return 0;
    
    const now = new Date();
    const trialEnd = subscription.trialEnd;
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  /**
   * Context value
   */
  const value: SubscriptionContextType = {
    // State
    subscription,
    loading,
    error,

    // Status checks
    isSubscriptionActive,
    hasFeatureAccess,
    canAccessTier,
    isTrialing,
    isCanceled,

    // Actions
    subscribe,
    changePlan,
    cancelPlan,
    reactivatePlan,
    openCustomerPortal,
    refreshSubscription,

    // Utilities
    getCurrentTier,
    getDaysUntilRenewal,
    getTrialDaysRemaining,
    pendingDowngradeTier,
    pendingDowngradeEffectiveDate,
    cancelScheduledDowngrade: async () => {
      const { cancelScheduledDowngrade } = await import('../services/stripe/subscriptions');
      await cancelScheduledDowngrade();
      await loadSubscription();
    },
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to use subscription context
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
